import type { Justica } from '@/api/calendarioForense';
import type { PrazoCalculo } from '@/api/client';
import { FeriadoForm, type LugarDoFormulario } from '@/components/feriados/FeriadoForm';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useLancarFeriado } from '@/hooks/useMeusFeriados';

/** Os escopos que a conta permite, a partir do lugar do ato gravado nela. */
export function lugaresPossiveis(lugar: PrazoCalculo['lugar']): LugarDoFormulario[] {
  if (!lugar) return [];
  const vazio = { tribunal: null, municipio_ibge: null, uf: null, justica: null };
  const lugares: LugarDoFormulario[] = [];
  if (lugar.tribunal) {
    lugares.push({
      value: 'tribunal',
      label: `Todo o ${lugar.tribunal}`,
      lugar: { ...vazio, abrangencia: 'tribunal', tribunal: lugar.tribunal },
    });
  }
  if (lugar.municipio_ibge && lugar.justica) {
    // No ato de 2º grau, o município da conta é o da sede do tribunal, não a comarca de origem.
    const onde = lugar.segundo_grau ? 'no município da sede do tribunal' : 'na comarca do processo';
    lugares.push({
      value: 'municipal',
      label: lugar.tribunal ? `Só ${onde}, no ${lugar.tribunal}` : `Só ${onde}`,
      lugar: {
        ...vazio,
        abrangencia: 'municipal',
        municipio_ibge: lugar.municipio_ibge,
        justica: lugar.justica as Justica,
      },
    });
  }
  if (lugar.uf) {
    lugares.push({
      value: 'estadual',
      label: `Todo o estado (${lugar.uf}), em qualquer justiça`,
      lugar: { ...vazio, abrangencia: 'estadual', uf: lugar.uf },
    });
  }
  return lugares;
}

interface Props {
  calculo: PrazoCalculo;
  onClose: () => void;
}

/**
 * "Faltou um feriado ou uma suspensão?" (Bloco E): o advogado lança o dia que a
 * conta não pegou. Vale na hora, só nas contas dele, e vira sugestão ao
 * calendário oficial.
 */
export function LancarFeriadoDialog({ calculo, onClose }: Props) {
  const lancar = useLancarFeriado();
  const lugares = lugaresPossiveis(calculo.lugar);

  return (
    // Fechar com o envio em curso perderia o erro (o callback não roda desmontado).
    <Dialog open onOpenChange={(open) => !open && !lancar.isPending && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Faltou um feriado ou uma suspensão?</DialogTitle>
          <DialogDescription>
            Vale na hora e só nas suas contas: os vencimentos são refeitos e a agenda acompanha. O JurisApp confere o
            ato e pode passar a contar o dia para todos.
          </DialogDescription>
        </DialogHeader>

        {lugares.length === 0 ? (
          <Alert>
            <AlertDescription>
              Esta conta não sabe o tribunal nem o lugar do processo. Sem eles, não dá para lançar o dia aqui.
            </AlertDescription>
          </Alert>
        ) : (
          <FeriadoForm
            lugares={lugares}
            lugarInicial={lugares[0].value}
            lancar={lancar}
            onLancado={onClose}
            onCancelar={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
